import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Typography, Steps, Result } from 'antd';
import { LockOutlined, MailOutlined, BuildOutlined, IdcardOutlined } from '@ant-design/icons';
import { signUp, confirmSignUp } from '../auth';

const { Text, Title } = Typography;

interface RegistroValues {
  email: string;
  password: string;
  confirmPassword: string;
  nombreEmpresa: string;
  rfc: string;
}

export function Registro() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(values: RegistroValues) {
    setError(null);
    if (values.password !== values.confirmPassword) {
      setError('Las Contraseñas No Coinciden');
      return;
    }
    setLoading(true);
    try {
      await signUp(
        values.email.trim(),
        values.password,
        values.nombreEmpresa.trim(),
        values.rfc.trim().toUpperCase(),
      );
      setEmail(values.email.trim());
      setStep(1);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'No se Pudo Crear la Cuenta';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  async function onConfirm(values: { code: string }) {
    setError(null);
    setLoading(true);
    try {
      await confirmSignUp(email, values.code.trim());
      setStep(2);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Código Inválido';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      background: 'linear-gradient(135deg, #1a3a5c 0%, #0d6efd 100%)',
    }}>
      <Card style={{ width: 420, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <img
            src="/logo-asicontadores-color.png"
            alt="ASI Contadores"
            style={{ height: 72, width: 'auto' }}
          />
          <Title level={4} style={{ marginTop: 8, marginBottom: 0 }}>
            Crear Cuenta
          </Title>
          <Text type="secondary">
            Registro para acceso a la plataforma
          </Text>
        </div>

        <Steps current={step} size="small" style={{ marginBottom: 24 }}>
          <Steps.Step title="Datos" />
          <Steps.Step title="Verificar" />
          <Steps.Step title="Listo" />
        </Steps>

        {error && (
          <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />
        )}

        {step === 0 && (
          <Form<RegistroValues> layout="vertical" onFinish={onSubmit}>
            <Form.Item
              name="email"
              label="Correo Electrónico"
              rules={[
                { required: true, message: 'Ingresa tu Correo' },
                { type: 'email', message: 'Correo Inválido' },
              ]}
            >
              <Input
                size="large"
                prefix={<MailOutlined />}
                placeholder="usuario@empresa.com"
              />
            </Form.Item>
            <Form.Item
              name="nombreEmpresa"
              label="Nombre de Empresa"
              rules={[{ required: true, message: 'Ingresa el Nombre de tu Empresa' }]}
            >
              <Input
                size="large"
                prefix={<BuildOutlined />}
                placeholder="Mi Empresa S.A. de C.V."
              />
            </Form.Item>
            <Form.Item
              name="rfc"
              label="RFC"
              rules={[
                { required: true, message: 'Ingresa tu RFC' },
                { len: 13, message: 'El RFC debe tener 13 caracteres' },
              ]}
            >
              <Input
                size="large"
                prefix={<IdcardOutlined />}
                placeholder="ABCD010203ABC"
                maxLength={13}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>
            <Form.Item
              name="password"
              label="Contraseña"
              rules={[
                { required: true, message: 'Ingresa una Contraseña' },
                { min: 8, message: 'Mínimo 8 caracteres' },
              ]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Mínimo 8 caracteres"
              />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="Confirmar Contraseña"
              rules={[{ required: true, message: 'Confirma tu Contraseña' }]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Repite la Contraseña"
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
              Crear Cuenta
            </Button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Text type="secondary">
                ¿Ya tienes cuenta?{' '}
                <Link to="/login" style={{ color: '#0d6efd' }}>
                  Iniciar Sesión
                </Link>
              </Text>
            </div>
          </Form>
        )}

        {step === 1 && (
          <Form layout="vertical" onFinish={onConfirm}>
            <Alert
              type="info"
              showIcon
              message={`Te enviamos un código a ${email}`}
              description="Ingresa el código de verificación que recibiste por correo."
              style={{ marginBottom: 16 }}
            />
            <Form.Item
              name="code"
              label="Código de Verificación"
              rules={[{ required: true, message: 'Ingresa el Código' }]}
            >
              <Input size="large" placeholder="123456" maxLength={6} />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
              Verificar Cuenta
            </Button>
          </Form>
        )}

        {step === 2 && (
          <Result
            status="success"
            title="Cuenta Creada"
            subTitle="Tu cuenta está lista. Ahora puedes iniciar sesión."
            extra={[
              <Button type="primary" key="login" onClick={() => navigate('/login')}>
                Iniciar Sesión
              </Button>,
            ]}
          />
        )}
      </Card>
    </div>
  );
}
